import { Location } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FusionService } from '@shared-angular/services/fusion.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { LoginService } from '@shared-angular/services/login.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { PopoverModule } from 'primeng/popover';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { BotPreviewService } from '../shared/services/bot-preview.service';
import { BotService } from '../shared/services/bot.service';
import { RouteShareService } from '../shared/services/route-share.service';

type DebugPlacement = 'side-by-side' | 'new-window';

type PreviewViewItem = {
  label: string;
  icon: string;
  url: string;
};

type PreviewPreference = {
  selectedViewUrl: string;
  selectedViewLabel: string;
  debugMode: boolean;
  debugPlacement: DebugPlacement;
};

@Component({
  selector: 'app-preview',
  imports: [FormsModule, ButtonModule, ButtonGroupModule, PopoverModule, SelectButtonModule, TooltipModule],
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss']
})
export class PreviewComponent implements OnDestroy {

  public viewItems: PreviewViewItem[] = [];
  public preference: PreviewPreference;

  public readonly debugOptions = [
    { label: $localize`:@@preview-debug-toggle-off:Aus`, value: false },
    { label: $localize`:@@preview-debug-toggle-on:An`, value: true }
  ];

  public readonly placementOptions = [
    { label: $localize`:@@preview-debug-placement-side:Nebeneinander`, icon: 'ri-layout-column-line', value: 'side-by-side' as DebugPlacement },
    { label: $localize`:@@preview-debug-placement-window:Im neuen Fenster`, icon: 'ri-window-line', value: 'new-window' as DebugPlacement }
  ];

  private previewWindowRef: WindowProxy | null = null;
  private debugWindowRef: WindowProxy | null = null;
  private fusionSubs: Subscription[] = [];

  constructor(
    private langService: LanguageService,
    private rss: RouteShareService,
    private fusionService: FusionService,
    private botService: BotService,
    private loginService: LoginService,
    private toastr: ToastrService,
    private location: Location,
    private botPreview: BotPreviewService
  ) {
    this.preference = this.loadPreference();
    this.buildViewItems();

    this.fusionSubs.push(
      this.fusionService.onFusionConfigCreated.subscribe(() => this.buildViewItems()),
      this.fusionService.onFusionConfigDeleted.subscribe(() => this.buildViewItems())
    );
  }

  public ngOnDestroy(): void {
    this.fusionSubs.forEach(s => s.unsubscribe());
  }

  public get debugEnabled(): boolean {
    return this.preference.debugMode;
  }

  public isSelected(url: string): boolean {
    return this.preference.selectedViewUrl === url;
  }

  public openPreview(url?: string, label?: string): void {
    if (url != null && label != null) {
      this.preference = { ...this.preference, selectedViewUrl: url, selectedViewLabel: label };
      this.savePreference(this.preference);
    }
    this.applyDebugViewMode();
  }

  public setDebugEnabled(enabled: boolean): void {
    if (!enabled) {
      this.closeDebugWindow();
    }
    this.preference = { ...this.preference, debugMode: enabled };
    this.savePreference(this.preference);
  }

  public setDebugPlacement(placement: DebugPlacement): void {
    if (placement !== 'new-window') {
      this.closeDebugWindow();
    }
    this.preference = { ...this.preference, debugPlacement: placement };
    this.savePreference(this.preference);
  }

  private closeDebugWindow(): void {
    if (this.debugWindowRef != null && !this.debugWindowRef.closed) {
      this.debugWindowRef.close();
    }
    this.debugWindowRef = null;
  }

  private applyDebugViewMode(): void {
    const base = this.preference.selectedViewUrl;
    const botId = this.rss.botConfig._id;
    const mode = this.debugEnabled ? this.preference.debugPlacement : 'off';

    if (mode === 'side-by-side') {
      this.closeDebugWindow();
      const splitUrl = this.location.prepareExternalUrl(`/debug-split/${botId}?previewUrl=${encodeURIComponent(base)}`);
      this.updateOrOpenPreviewWindow(() => Promise.resolve(splitUrl));
    } else if (mode === 'new-window') {
      this.updateOrOpenPreviewWindow(() => this.botPreview.previewUrl(botId, base));
      const debugUrl = this.location.prepareExternalUrl(`/debug/${botId}`);
      if (this.debugWindowRef == null || this.debugWindowRef.closed) {
        this.debugWindowRef = window.open(debugUrl, `_bot_debug_${botId}`, 'width=900,height=700') ?? null;
      } else {
        this.debugWindowRef.focus();
      }
    } else {
      this.closeDebugWindow();
      this.updateOrOpenPreviewWindow(() => this.botPreview.previewUrl(botId, base));
    }
  }

  private updateOrOpenPreviewWindow(resolveUrl: () => Promise<string>): void {
    const win = this.botPreview.openWindow('_bot_preview', this.previewWindowRef);
    this.previewWindowRef = win;
    if (win == null) return;
    resolveUrl().then(url => {
      win.location.href = url;
      win.focus();
    }).catch(() => this.toastr.error('LOADED'));
  }

  public async resetBot(): Promise<void> {
    await this.botService.resetBot(this.rss.botConfig._id);
    if (this.previewWindowRef != null && !this.previewWindowRef.closed) {
      // Cross-origin window: reload() is not allowed, a fresh preview URL triggers the reload instead.
      this.applyDebugViewMode();
    }
    this.toastr.success('RESET');
  }

  public buildViewItems(): void {
    const lang = this.langService.getLanguage();
    const botId = this.rss.botConfig._id;

    const items: PreviewViewItem[] = [
      {
        label: $localize`:@@preview-chat:Chat Vorschau`,
        icon: 'ri-chat-3-line',
        url: `/${lang}/bot/${botId}/chat`
      },
      {
        label: $localize`:@@preview-audio:Audio Vorschau`,
        icon: 'ri-mic-line',
        url: `/${lang}/bot/${botId}/audio`
      },
      {
        label: $localize`:@@preview-avatar:Avatar Nova Vorschau`,
        icon: 'ri-user-3-line',
        url: `/${lang}/bot/${botId}/avatar-nova`
      },
      {
        label: $localize`:@@preview-did-avatar:Avatar Vega Vorschau`,
        icon: 'ri-user-3-line',
        url: `/${lang}/bot/${botId}/avatar-vega`
      }
    ];

    if (Array.isArray(this.fusionService.fusionContent) && this.fusionService.fusionContent.length > 0) {
      for (const f of this.fusionService.fusionContent) {
        items.push({
          label: `(Fusion) ${f.metadata.name} – ${f.metadata.type} – ${f.metadata.bottype}`,
          icon: 'ri-layout-line',
          url: `/${lang}/fusion/${botId}/${f._id}/`
        });
      }
    }

    this.viewItems = items;
  }

  private getPreferenceKey(): string {
    const userId = this.loginService.loggedInUser?._id ?? 'anonymous';
    const botId = this.rss.botConfig._id;
    return `preview:${userId}:${botId}`;
  }

  private loadPreference(): PreviewPreference {
    const lang = this.langService.getLanguage();
    const botId = this.rss.botConfig._id;
    const defaultPref: PreviewPreference = {
      selectedViewUrl: `/${lang}/bot/${botId}/chat`,
      selectedViewLabel: $localize`:@@preview-chat:Chat Vorschau`,
      debugMode: false,
      debugPlacement: 'side-by-side'
    };

    try {
      const stored = localStorage.getItem(this.getPreferenceKey());
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<PreviewPreference> & { debugViewMode?: string };
        // Strip legacy ?debug=true from stored URLs
        if (parsed.selectedViewUrl) {
          parsed.selectedViewUrl = parsed.selectedViewUrl.replace('?debug=true', '');
        }
        const pref = { ...defaultPref, ...parsed };
        // Legacy: gespeicherter debugViewMode wird auf debugMode/debugPlacement abgebildet
        if (parsed.debugViewMode != null && parsed.debugViewMode !== 'off') {
          pref.debugMode = true;
          pref.debugPlacement = parsed.debugViewMode as DebugPlacement;
        }
        return pref;
      }
    } catch {
      // Ignore parse errors
    }
    return defaultPref;
  }

  private savePreference(pref: PreviewPreference): void {
    try {
      localStorage.setItem(this.getPreferenceKey(), JSON.stringify(pref));
    } catch {
      // Ignore storage errors
    }
  }
}
